/**
 * LOBBERO STAYS — the guest's phone as WhatsApp / Viber links (ai-plans/stays
 * plan S8, R-S8.23). The twin of lobbero_be/src/stays/messages/phone-links.js
 * over the same cases (stays-phone-links.cases.json, a COPY kept by
 * check-fe-copy.js). The server already answers these links with each
 * composed message; this twin rebuilds them when the host edits the text.
 *
 * Keep digits and a leading "+". "+…" → the digits after "+"; "00…" → the
 * digits after "00"; anything else has no country code. Valid at 8–15 digits.
 * Viber's form is Konios's and is not in Viber's documentation (S8-Q2: shown,
 * "check on a real phone").
 */

export type PhoneWarning = 'NO_PHONE' | 'NO_COUNTRY_CODE' | 'INVALID_PHONE';

export interface NormalizedPhone {
  digits: string | null;
  warning: PhoneWarning | null;
}

export const normalizePhone = (raw: string | null | undefined): NormalizedPhone => {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return { digits: null, warning: 'NO_PHONE' };
  const kept = s.replace(/[^\d+]/g, '');
  let digits: string;
  if (kept.startsWith('+')) digits = kept.slice(1).replace(/\D/g, '');
  else if (kept.startsWith('00')) digits = kept.slice(2).replace(/\D/g, '');
  else return { digits: null, warning: 'NO_COUNTRY_CODE' };
  if (digits.length < 8 || digits.length > 15) return { digits: null, warning: 'INVALID_PHONE' };

  return { digits, warning: null };
};

export const whatsappLink = (digits: string | null, text: string): string => {
  const t = encodeURIComponent(text || '');

  return digits ? `https://wa.me/${digits}?text=${t}` : `https://wa.me/?text=${t}`;
};

export const viberLink = (digits: string | null, text: string): string =>
  digits ? `viber://chat?number=%2B${digits}` : `viber://forward?text=${encodeURIComponent(text || '')}`;

export const phoneLinks = (
  raw: string | null | undefined,
  text: string
): { phone: NormalizedPhone; links: { whatsapp: string; viber: string } } => {
  const phone = normalizePhone(raw);

  return { phone, links: { whatsapp: whatsappLink(phone.digits, text), viber: viberLink(phone.digits, text) } };
};
